import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { BrandSelectHandler, SelectedBrand, SidebarBrand } from ".";

interface ChildBrandItemProps {
  brand: SidebarBrand;
  selectedBrand: SelectedBrand;
  onBrandSelect: BrandSelectHandler;
  depth?: number;
}

// Recursive component for rendering child brand nodes
const ChildBrandItem: React.FC<ChildBrandItemProps> = ({
  brand,
  selectedBrand,
  onBrandSelect,
  depth = 0,
}) => {
  const [open, setOpen] = useState(true);
  const hasChildren = brand.children.length > 0;
  const isSelected = selectedBrand.brandId === brand.id
    && selectedBrand.showChildrenStatistics === (!!brand.children.length);


  const handleClick = () => onBrandSelect(
    brand.id,
    !!brand.children.length
  );

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  // Calculate padding based on depth
  const paddingLeft = 6 + depth * 2;

  const plainParentBrand = useMemo(() => {
    const newBrand = { ...brand };
    newBrand.children = [];
    return newBrand
  }, [brand])

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleClick}
          sx={{
            pl: paddingLeft,
            pr: 3,
            py: 1,
            minHeight: 36,
            bgcolor: isSelected ? "#0E6FFF0D" : "transparent",
            color: isSelected ? "#0E6FFF" : "#5C6B80",
            "&:hover": {
              bgcolor: isSelected ? "#0E6FFF0D" : "#F5F7FA",
            },
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {brand.name || brand.domain || "Brand"}
          </Box>
          {hasChildren && (
            <Box
              component="span"
              onClick={handleToggle}
              sx={{ display: "flex", alignItems: "center", ml: 1 }}
            >
              {open
                ? <ExpandLess sx={{ fontSize: 16 }} />
                : <ExpandMore sx={{ fontSize: 16 }} />
              }
            </Box>
          )}
        </ListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ChildBrandItem
              key={plainParentBrand.id}
              brand={plainParentBrand}
              selectedBrand={selectedBrand}
              onBrandSelect={onBrandSelect}
              depth={depth + 1}
            />
            {brand.children.map((child) => (
              <ChildBrandItem
                key={child.id}
                brand={child}
                selectedBrand={selectedBrand}
                onBrandSelect={onBrandSelect}
                depth={depth + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

interface BrandSidebarItemProps {
  brand: SidebarBrand;
  childBrands: SidebarBrand[];
  selectedBrand: SelectedBrand;
  expanded: boolean; // sidebar expanded state
  onBrandSelect: (
    brandId: string | null,
    showChildrenStatistics?: boolean,
  ) => void;
}

const BrandSidebarItem: React.FC<BrandSidebarItemProps> = ({
  brand,
  childBrands,
  selectedBrand,
  expanded,
  onBrandSelect,
}) => {
  const parentWithoutChildren = useMemo(() => {
    if (brand.id === null) return null;
    const newParent = { ...brand };
    newParent.children = [];
    return newParent
  }, [brand])

  const [open, setOpen] = useState(true);

  // Header is selected when selectedBrandId is null (rollup mode)
  const isHeaderSelected = selectedBrand.brandId === brand.id
    && selectedBrand.showChildrenStatistics === !!brand.children.length

  const handleToggle = () => {
    setOpen(!open);
  };

  if (!expanded) return null;

  return (
    <>
      {/* Brand selector dropdown - styled as a gray pill/dropdown */}
      <Box p={1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            bgcolor: isHeaderSelected ? "#0E6FFF0D" : "#F5F7FA",
            borderRadius: "8px",
          }}
        >
          <Box
            component="span"
            onClick={() => onBrandSelect(brand.id, !!brand.children.length)}
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: isHeaderSelected ? "#0E6FFF" : "#0B1620",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
              cursor: "pointer",
              "&:hover": {
                color: "#0E6FFF",
              },
            }}
          >
            {brand.name || brand.domain || "Brand"}
          </Box>
          {/* Arrow - click to toggle drawer */}
          <Box
            component="span"
            onClick={handleToggle}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 0.5,
              borderRadius: "4px",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.08)",
              },
            }}
          >
            {open ? <ExpandLess sx={{ fontSize: 18, color: "#5C6B80" }} /> : <ExpandMore sx={{ fontSize: 18, color: "#5C6B80" }} />}
          </Box>
        </Box>
      </Box>

      {/* Expanded content */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>

          {parentWithoutChildren && (
            <ChildBrandItem
              brand={parentWithoutChildren}
              selectedBrand={selectedBrand}
              onBrandSelect={onBrandSelect}
              depth={0}
            />
          )}
          {/* Child brands - recursive tree */}
          {childBrands.map((child) => (
            <ChildBrandItem
              key={child.id}
              brand={child}
              selectedBrand={selectedBrand}
              onBrandSelect={onBrandSelect}
              depth={0}
            />
          ))}

        </List>
      </Collapse>
    </>
  );
};

export default BrandSidebarItem;
